import React, { useEffect, useState } from 'react';
import {
  cssColorValue,
  LogoSpotify,
  semanticColors,
  spacer16,
  spacer8,
  TextLink,
} from '@spotify-internal/encore-web/core';
import styled from 'styled-components';

import Ellipsis from 'components/Ellipsis';
import { MastheadHeaderData, MastheadLinkList } from 'lib/sanity/types';
import { useUbiMastheadHeader } from 'utils/ubi/pages/useUbiHeader';
import { SideMenuWithProfileLinksMainNavList } from 'components/Masthead/Header/SideMenu/withProfileLinks/MainNavList';
import { SideMenuWithProfileLinksSubNavList } from 'components/Masthead/Header/SideMenu/withProfileLinks/SubNavList';
import { SideMenuNavigationList } from 'components/Masthead/Header/SideMenu/Shared';

const StyledEllipsis = styled(Ellipsis)`
  margin-bottom: ${spacer16};
  margin-left: ${spacer8};
`;

const LogoWrapper = styled(TextLink)`
  && {
    text-decoration: none;
    width: fit-content;

    &:focus {
      box-shadow: none;
      transition: none;
      outline: 5px auto -webkit-focus-ring-color;
    }
  }
`;

const StyledLogo = styled(LogoSpotify)`
  color: ${cssColorValue(semanticColors.textBase)};
  fill: ${cssColorValue(semanticColors.textBase)};
  height: 24px;
  width: 78px;
`;

type Props = {
  headerEntry: MastheadHeaderData;
  closeBackdrop: () => void;
  username?: string;
};

export const NavBarSideMenuWithProfileLinks = ({ headerEntry, closeBackdrop, username }: Props) => {
  const ubi = useUbiMastheadHeader();

  const [subMenu, setSubMenu] = useState<MastheadLinkList | null>(null);

  const onProfileLinkClick = (identifier: string, uri: string) => {
    ubi.logProfileLinkInteraction({ container: 'sidemenu', identifier, uri });
  };

  const handleSetSubMenu = (value: MastheadLinkList | null) => {
    setSubMenu(value);
  };

  const isLoggedIn = username !== undefined;

  useEffect(() => {
    ubi.logProfileLinksContainerImpression('sidemenu');
  }, [ubi]);

  return (
    <>
      <StyledEllipsis variant="titleLarge">{username ? `Hi ${username}` : `Hello`}!</StyledEllipsis>
      <SideMenuNavigationList $variant="withProfileLinks">
        {subMenu ? (
          <SideMenuWithProfileLinksSubNavList
            closeBackdrop={closeBackdrop}
            closeSubMenu={() => {
              handleSetSubMenu(null);
            }}
            isLoggedIn={isLoggedIn}
            subMenu={subMenu}
          />
        ) : (
          <SideMenuWithProfileLinksMainNavList
            closeBackdrop={closeBackdrop}
            openSubMenu={(menu: MastheadLinkList) => {
              handleSetSubMenu(menu);
            }}
            isLoggedIn={isLoggedIn}
            onProfileLinkClick={onProfileLinkClick}
            headerEntry={headerEntry}
          />
        )}
      </SideMenuNavigationList>
      <LogoWrapper href="https://www.spotify.com" data-testid="navbar_side_menu_logo">
        <StyledLogo />
      </LogoWrapper>
    </>
  );
};
